import { useEffect, useState } from 'react';

import { Button } from '../../../../../components/ui/Button';
import { Dialog } from '../../../../../components/ui/Dialog';
import { Icon } from '../../../../../components/ui/Icon';
import { Tabs } from '../../../../../components/ui/Tabs';
import styles from './AiRecordDialog.module.css';

/*
 * AI 记录详情侧拉抽屉（沟通记录卡「查看 AI 记录」入口）。
 * 结构对齐设计稿与移动端 ai-record 模块：记录标题（仅查看）→ 元信息 →
 * 录音回听条 → AI 总结 / 完整转写页签 → 底部免责提示。
 * 录音为无声时间轴模拟（与移动端 AudioPreview 同策略），播放与暂停均使用本地 Icon。
 */

const DURATION = 12 * 60 + 26;
const SPEEDS = [1, 1.5, 2] as const;

function clockTime(seconds: number): string {
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
}

const summarySections = [
  {
    key: 'core',
    icon: 'list-check',
    title: '核心摘要',
    body: '家长咨询数学辅导，已预约周六上午10点试听，报名尚未确定。',
  },
  {
    key: 'process',
    icon: 'bubble',
    title: '沟通过程',
    body: '家长反馈孩子近期数学成绩下滑，希望巩固基础。老师介绍课程安排，家长关注费用和班额，需与家人商量。',
  },
  {
    key: 'todo',
    icon: 'circle-check',
    title: '待跟进事项',
    body: '今天发送课程安排、班额和费用说明，周五确认试听。',
  },
] as const;

const transcriptSegments = [
  { speaker: '01', name: '陈清和老师', at: 0, text: '喂，您好，是知夏妈妈吗？我是校管家的陈清和老师，想跟您聊聊孩子的数学学习。' },
  { speaker: '02', name: '苏晚晴（妈妈）', at: 14, text: '您好您好。孩子最近数学成绩有些下滑，想找个班巩固一下基础。' },
  { speaker: '01', name: '陈清和老师', at: 31, text: '好的。孩子现在几年级？我们这边有同步巩固班，先做学情测评再定班型。' },
  { speaker: '02', name: '苏晚晴（妈妈）', at: 47, text: '四年级。周六上午十点试听可以吗？费用和班额麻烦发我看看，我和孩子爸爸商量下。' },
] as const;

export interface AiRecordDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** 记录标题、元信息由页面传入，与沟通记录卡保持同一份演示数据。 */
  recordTitle: string;
  operator: string;
  time: string;
  studentName: string;
}

export function AiRecordDialog({ open, onOpenChange, recordTitle, operator, time, studentName }: AiRecordDialogProps) {
  const [tab, setTab] = useState('summary');
  const [playing, setPlaying] = useState(false);
  const [position, setPosition] = useState(0);
  const [speedIndex, setSpeedIndex] = useState(0);
  const speed = SPEEDS[speedIndex];

  /* 无声播放模拟：按倍速每秒推进时间轴，到尾自动停（不发真音频）。 */
  useEffect(() => {
    if (!playing) return;
    const timer = window.setInterval(() => {
      setPosition((prev) => {
        if (prev >= DURATION) {
          setPlaying(false);
          return prev;
        }
        return Math.min(DURATION, prev + speed);
      });
    }, 1000);
    return () => window.clearInterval(timer);
  }, [playing, speed]);

  function togglePlay() {
    if (!playing && position >= DURATION) setPosition(0);
    setPlaying(!playing);
  }

  return <Dialog
    onOpenChange={(next) => { onOpenChange(next); if (!next) { setPlaying(false); setPosition(0); setTab('summary'); } }}
    open={open}
    side="right"
    title="AI 记录详情"
    width={640}
  >
    <div className={styles.content}>
      {/* 记录标题：仅查看（编辑入口只在移动端提供）。 */}
      <h2 className={styles.recordTitle}>{recordTitle}</h2>
      <p className={styles.meta}>
        <span>{time}</span>
        <span>记录人：{operator}</span>
        <span>关联学员：{studentName}</span>
      </p>

      <div className={styles.player}>
        <button aria-label={playing ? '暂停回放' : '播放录音'} className={styles.playButton} type="button" onClick={togglePlay}>
          <Icon name={playing ? 'pause-mini-fill' : 'play-large-fill'} size="base" />
        </button>
        <div className={styles.trackGroup}>
          <input
            aria-label="录音进度"
            className={styles.track}
            max={DURATION}
            min={0}
            style={{ '--playback-progress': `${Math.min(100, position / DURATION * 100)}%` } as React.CSSProperties}
            type="range"
            value={position}
            onChange={(event) => setPosition(Number(event.target.value))}
          />
          <div className={styles.trackTime}>{clockTime(position)} / {clockTime(DURATION)}</div>
        </div>
        <Button
          aria-label={`播放速度 ${speed} 倍`}
          className={styles.speedButton}
          type="default"
          onClick={() => setSpeedIndex((speedIndex + 1) % SPEEDS.length)}
        >{speed}×</Button>
        <Button className={styles.speedButton} leadingIcon="download-line" type="default" onClick={() => {}}>下载</Button>
      </div>

      <Tabs
        ariaLabel="AI 记录内容页签"
        className={styles.tabs}
        idPrefix="ai-record-detail"
        onChange={setTab}
        tabs={[
          { id: 'summary', label: 'AI 总结' },
          { id: 'transcript', label: '完整转写' },
        ]}
        value={tab}
      />

      {tab === 'summary'
        ? <div className={styles.panel}>
          {summarySections.map((section, index) => (
            <section className={index === 0 ? styles.coreSection : styles.summarySection} key={section.key}>
              <h3 className={styles.sectionTitle}>
                {index === 0 ? <Icon className={styles.sectionIcon} name="sparkling-2-fill" size="sm" /> : null}
                {section.title}
              </h3>
              <p className={styles.sectionBody}>{section.body}</p>
            </section>
          ))}
        </div>
        : <div className={styles.panel}>
          <p className={styles.transcriptHint}>点击文字定位回听</p>
          {transcriptSegments.map((segment) => (
            <div className={styles.segment} key={segment.at}>
              <div className={styles.segmentHead}>
                <span className={styles.speakerBadge}>{segment.speaker}</span>
                <span className={styles.speakerName}>{segment.name}</span>
                <span className={styles.segmentTime}>{clockTime(segment.at)}</span>
              </div>
              <button
                aria-label={`定位到 ${clockTime(segment.at)}`}
                className={styles.segmentText}
                type="button"
                onClick={() => setPosition(Math.min(DURATION, segment.at))}
              >{segment.text}</button>
            </div>
          ))}
        </div>}

      <p className={styles.notice}>
        AI 根据沟通内容生成，请核对关键信息
      </p>
    </div>
  </Dialog>;
}
